/**
 * Document Export Utilities
 * Functions to extract content from documents for export to Unity/Godot
 */

import { DocumentData, DocumentItem } from '@/lib/stores/documentStore';
import { Block } from '@/components/editor/Editor';

/**
 * Represents a single block of content extracted from a document
 */
export interface DocumentExportBlock {
  type: 'text' | 'image';
  content: string; // Text content (HTML or plain text) or image URL
  index: number;
}

/**
 * Represents the extracted content from a document
 */
export interface DocumentExportContent {
  documentId: string;
  documentName: string;
  blocks: DocumentExportBlock[];
}

/**
 * Strip HTML tags from a string, returning plain text
 * @param html - HTML string to strip
 * @returns Plain text without HTML tags
 */
export function stripHtmlTags(html: string): string {
  if (!html) return '';
  
  // Remove HTML tags
  let text = html.replace(/<[^>]*>/g, ' ');
  
  // Decode HTML entities
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");
  
  // Normalize whitespace
  text = text.replace(/\s+/g, ' ').trim();
  
  return text;
}

/**
 * Extract blocks from a single DocumentData section
 * @param section - DocumentData section containing blocks
 * @param startIndex - Starting index for block numbering
 * @param stripHtml - Whether to strip HTML tags from text content
 * @returns Array of DocumentExportBlock
 */
function extractBlocksFromSection(
  section: DocumentData,
  startIndex: number = 0,
  stripHtml: boolean = false
): DocumentExportBlock[] {
  const blocks: DocumentExportBlock[] = [];
  
  if (!section.blocks || !Array.isArray(section.blocks)) {
    return blocks;
  }
  
  section.blocks.forEach((block: Block, index: number) => {
    if (block.type === 'text') {
      // Text block - extract content
      const content = stripHtml ? stripHtmlTags(block.content) : block.content;
      
      // Skip empty text blocks
      if (content.trim()) {
        blocks.push({
          type: 'text',
          content,
          index: startIndex + index
        });
      }
    } else if (block.type === 'image') {
      // Image block - extract src from metadata
      const imageSrc = block.metadata?.src || '';
      
      if (imageSrc) {
        blocks.push({
          type: 'image',
          content: imageSrc,
          index: startIndex + index
        });
      }
    }
    // Other block types can be handled here in the future
  });
  
  return blocks;
}

/**
 * Extract content from a document for export
 * @param document - DocumentItem to extract content from
 * @param stripHtml - Whether to strip HTML tags from text content
 * @returns DocumentExportContent with all blocks extracted
 */
export function extractDocumentContent(
  document: DocumentItem,
  stripHtml: boolean = false
): DocumentExportContent {
  const allBlocks: DocumentExportBlock[] = [];
  
  // Document data is stored as DocumentData[] (array of sections)
  if (document.data && Array.isArray(document.data)) {
    let currentIndex = 0;
    
    document.data.forEach((section: DocumentData) => {
      const sectionBlocks = extractBlocksFromSection(section, currentIndex, stripHtml);
      allBlocks.push(...sectionBlocks);
      currentIndex += sectionBlocks.length;
    });
  }
  
  return {
    documentId: document.id,
    documentName: document.name,
    blocks: allBlocks
  };
}

/**
 * Extract content from document data (without full DocumentItem)
 * Useful when you only have the data portion
 * @param data - DocumentData array from a document
 * @param stripHtml - Whether to strip HTML tags from text content
 * @returns Array of DocumentExportBlock
 */
export function extractBlocksFromDocumentData(
  data: DocumentData[],
  stripHtml: boolean = false
): DocumentExportBlock[] {
  const allBlocks: DocumentExportBlock[] = [];
  
  if (!data || !Array.isArray(data)) {
    return allBlocks;
  }
  
  let currentIndex = 0;
  
  data.forEach((section: DocumentData) => {
    const sectionBlocks = extractBlocksFromSection(section, currentIndex, stripHtml);
    allBlocks.push(...sectionBlocks);
    currentIndex += sectionBlocks.length;
  });
  
  return allBlocks;
}

/**
 * Format document content for C# export (Unity)
 * @param content - DocumentExportContent to format
 * @returns C# array initializer string
 */
export function formatDocumentContentForCSharp(content: DocumentExportContent): string {
  if (content.blocks.length === 0) {
    return 'new DocumentBlock[] { }';
  }
  
  const blockStrings = content.blocks.map(block => {
    const escapedContent = block.content
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r');
    
    return `new DocumentBlock { type = "${block.type}", content = "${escapedContent}", index = ${block.index} }`;
  });
  
  return `new DocumentBlock[] { ${blockStrings.join(', ')} }`;
}

/**
 * Format document content for GDScript export (Godot)
 * @param content - DocumentExportContent to format
 * @returns GDScript array string
 */
export function formatDocumentContentForGDScript(content: DocumentExportContent): string {
  if (content.blocks.length === 0) {
    return '[]';
  }
  
  const blockStrings = content.blocks.map(block => {
    const escapedContent = block.content
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/\n/g, '\\n');
    
    return `{"type": "${block.type}", "content": "${escapedContent}", "index": ${block.index}}`;
  });
  
  return `[${blockStrings.join(', ')}]`;
}

/**
 * Convert document content to a simple JSON-serializable format
 * @param content - DocumentExportContent to convert
 * @returns Plain JavaScript array of block objects
 */
export function documentContentToJson(content: DocumentExportContent): Array<{type: string; content: string; index: number}> {
  return content.blocks.map(block => ({
    type: block.type,
    content: block.content,
    index: block.index
  }));
}
